/**
 * E02 · what BOTH surfaces derive on read from a CONVERTED behavior-experiment row.
 *
 * During coexistence there are no ES read models (plan 04 D3): the mobile app and the provider
 * portal read the legacy `behaviorExperiments` document that the inline back-projection keeps
 * current, and compute the lifecycle from the additive fields it carries. A CONVERTED row carries
 * neither `status` nor `observations` — conversion never rewrites row content, and plan 04 §4.1
 * lets post-conversion events split design/run/outcome for NEW activity only, so nothing may be
 * emitted for what the client recorded in the legacy single `outcome`.
 *
 * FOUNDER RULING 2026-09-02, which this module exists to keep in ONE place:
 *   • a converted experiment is always **`active`** — never `completed` inferred from its outcome;
 *   • that outcome's observation-shaped part IS the FIRST observation of the experiment, derived,
 *     read-only, carrying the reserved id `legacy-outcome`;
 *   • its completion-shaped part is not a completion at all — it is what the completion screens
 *     PRE-FILL from until a native `ExperimentCompleted` exists.
 *
 * The same rule lives in the domain fold (`@cc/domain` `stateOfSnapshot` / `legacyOutcomeObservation`,
 * which the deciders read). This module is its READ-side twin, deliberately structural rather than
 * typed to either generated schema, so `@cc/mobile` and `@cc/portal` can each pass their own row
 * type without a cast. Pure: no I/O, no clock read except the `now` a caller passes.
 */

/** The reserved id of the derived legacy-outcome observation — reserved, never minted. */
export const LEGACY_OUTCOME_OBSERVATION_ID = 'legacy-outcome';

export type BehaviorExperimentStatus = 'draft' | 'active' | 'completed';

export type DerivedEmotionRating = { id: number; strength: number };

/** A converted row's single legacy `outcome`, read as the experiment's first run. */
export type DerivedLegacyObservation = {
  observationId: typeof LEGACY_OUTCOME_OBSERVATION_ID;
  occurredAt: string | null;
  whatHappened: string | null;
  emotions: DerivedEmotionRating[];
  feelings: number[];
  behaviors: string | null;
  /** Never existed legacy-side — null rather than invented. */
  whichHypothesisHeld: null;
  rating: null;
  learnings: string | null;
  /** The marker every surface reads to render "recorded before the new experience" and hide Edit. */
  legacy: true;
};

type EmotionLike = { id?: number | null; strength?: number | null } | null;

export type DeriveOutcomeLike = {
  emotions?: ReadonlyArray<EmotionLike | undefined> | null;
  feelings?: ReadonlyArray<number | null | undefined> | null;
  behaviors?: string | null;
  otherPredictions?: string | null;
  newLearnings?: string | null;
  patientReflections?: string | null;
  therapistReflections?: string | null;
  meaningForOriginalBelief?: string | null;
  oldThoughtStrength?: number | null;
  newThought?: string | null;
  newThoughtStrength?: number | null;
} | null;

export type DeriveRowLike = {
  experimentId?: string | null;
  status?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  activatedAt?: string | null;
  completedAt?: string | null;
  outcome?: DeriveOutcomeLike;
  followUpExperimentsIdeas?: string | null;
};

const filled = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const ratings = (
  list: ReadonlyArray<EmotionLike | undefined> | null | undefined,
): DerivedEmotionRating[] =>
  (Array.isArray(list) ? list : [])
    .filter(
      (e): e is { id: number; strength?: number | null } =>
        !!e && typeof e.id === 'number',
    )
    .map((e) => ({
      id: e.id,
      strength: typeof e.strength === 'number' ? e.strength : 0,
    }));

const ints = (
  list: ReadonlyArray<number | null | undefined> | null | undefined,
): number[] =>
  (Array.isArray(list) ? list : []).filter(
    (n): n is number => typeof n === 'number',
  );

/** Does the legacy `outcome` hold anything a run would hold? (the derivation's precondition) */
export function legacyOutcomeHasObservationContent(
  row: DeriveRowLike | null | undefined,
): boolean {
  const outcome = row?.outcome;
  if (!outcome) return false;
  return (
    ratings(outcome.emotions).length > 0 ||
    ints(outcome.feelings).length > 0 ||
    filled(outcome.behaviors) ||
    filled(outcome.newLearnings) ||
    filled(outcome.otherPredictions)
  );
}

/**
 * The derived first observation of a CONVERTED row, or null. Three conditions, all required and all
 * identical to the domain fold's: the row is log-owned (`experimentId`), it holds no observations of
 * its own, and its legacy `outcome` carries run-like content.
 */
export function derivedLegacyObservation(
  row:
    | (DeriveRowLike & {
        observations?: ReadonlyArray<unknown> | null;
      })
    | null
    | undefined,
): DerivedLegacyObservation | null {
  if (!row?.experimentId) return null;
  if ((row.observations?.length ?? 0) > 0) return null;
  if (!legacyOutcomeHasObservationContent(row)) return null;
  const outcome = row.outcome ?? {};
  return {
    observationId: LEGACY_OUTCOME_OBSERVATION_ID,
    // a RECORDED moment, never invented (plan 04 §4.1 guard 1)
    occurredAt: row.updatedAt ?? row.createdAt ?? null,
    whatHappened: outcome.otherPredictions ?? null,
    emotions: ratings(outcome.emotions),
    feelings: ints(outcome.feelings),
    behaviors: outcome.behaviors ?? null,
    whichHypothesisHeld: null,
    rating: null,
    learnings: outcome.newLearnings ?? null,
    legacy: true,
  };
}

/**
 * Is this observation the derived, read-only one? (the Edit action's guard on both surfaces)
 * Takes `unknown` so a screen can ask it of a union of its generated observation type and
 * `DerivedLegacyObservation` without narrowing first — the id is the whole test.
 */
export const isLegacyOutcomeObservation = (observation: unknown): boolean =>
  !!observation &&
  typeof observation === 'object' &&
  (observation as { observationId?: unknown }).observationId ===
    LEGACY_OUTCOME_OBSERVATION_ID;

/**
 * Status ON READ. The projection's own `status` when the row carries one; otherwise a converted row
 * (identity, no status yet) is **`active`** — a converted experiment is never read as completed
 * (founder ruling 2026-09-02). A row the log does not own has no E-era status at all: the caller
 * decides what to show for it (the portal shows nothing, the mobile list falls back to `active`).
 */
export function behaviorExperimentStatusOnRead(
  row: DeriveRowLike | null | undefined,
): BehaviorExperimentStatus | undefined {
  if (!row) return undefined;
  if (
    row.status === 'draft' ||
    row.status === 'active' ||
    row.status === 'completed'
  ) {
    return row.status;
  }
  if (!row.experimentId) return undefined;
  return 'active';
}

/**
 * The observations a screen shows: the row's own runs, or — for a converted row with none — the one
 * derived from its legacy `outcome`, first in the list.
 */
export function behaviorExperimentObservationsOnRead<T>(
  row:
    | (DeriveRowLike & { observations?: ReadonlyArray<T> | null })
    | null
    | undefined,
): Array<T | DerivedLegacyObservation> {
  const own = row?.observations ?? [];
  if (own.length > 0) return [...own];
  const derived = derivedLegacyObservation(row);
  return derived ? [derived] : [];
}

/** Which way round the observations read — a PRESENTATION preference, never persisted. */
export type BehaviorExperimentObservationOrder = 'oldest' | 'newest';

/**
 * The observations in the reader's chosen order — PURE, client-side, by `occurredAt`, with the
 * RECORDED order as the tie-break so two runs saved in the same second never swap on a re-render.
 * The DERIVED legacy observation of a converted row participates like any other entry (its
 * `occurredAt` is the row's `updatedAt ?? createdAt`).
 *
 * Founder ruling 2026-09-02 (E02-BACKEND-HANDOFF §13): the order toggle is pure presentation —
 * default OLDEST first, alternative NEWEST first, nothing stored, no refetch, no command. The
 * portal shipped its own copy of this sort first (`pages/behaviorExperiments/esHelpers.ts`
 * `sortObservations`); this is that rule moved where BOTH surfaces can read it.
 */
export function sortBehaviorExperimentObservations<
  T extends { occurredAt?: unknown },
>(
  observations: ReadonlyArray<T>,
  order: BehaviorExperimentObservationOrder,
): T[] {
  const at = (observation: T): number => {
    const ms = new Date((observation.occurredAt as string) ?? 0).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };
  const sign = order === 'newest' ? -1 : 1;
  return (Array.isArray(observations) ? observations : [])
    .map((observation: T, index: number) => ({ observation, index }))
    .sort((left, right) => {
      const byTime = at(left.observation) - at(right.observation);
      return sign * (byTime !== 0 ? byTime : left.index - right.index);
    })
    .map((entry) => entry.observation);
}

/** The observation count a screen shows — the derived one counts (founder ruling 2026-09-02). */
export function behaviorExperimentObservationCountOnRead(
  row:
    | (DeriveRowLike & { observations?: ReadonlyArray<unknown> | null })
    | null
    | undefined,
): number {
  return behaviorExperimentObservationsOnRead(row).length;
}

/**
 * The completion-shaped leaves a converted row's legacy `outcome` carried — the PRE-FILL for the
 * completion screens while the experiment has never been completed natively. `newLearnings` is
 * absent on purpose: it is the run-like leaf and travels as the derived observation's `learnings`.
 */
export type LegacyReflectionPrefill = {
  patientReflections: string | null;
  therapistReflections: string | null;
  meaningForOriginalBelief: string | null;
  oldThoughtStrength: number | null;
  newThought: string | null;
  newThoughtStrength: number | null;
  followUpExperimentsIdeas: string | null;
};

export function legacyReflectionPrefill(
  row: DeriveRowLike | null | undefined,
): LegacyReflectionPrefill {
  const outcome = row?.outcome ?? {};
  return {
    patientReflections: outcome.patientReflections ?? null,
    therapistReflections: outcome.therapistReflections ?? null,
    meaningForOriginalBelief: outcome.meaningForOriginalBelief ?? null,
    oldThoughtStrength: outcome.oldThoughtStrength ?? null,
    newThought: outcome.newThought ?? null,
    newThoughtStrength: outcome.newThoughtStrength ?? null,
    followUpExperimentsIdeas: row?.followUpExperimentsIdeas ?? null,
  };
}
